(function(angular, _) {
    /**
     * @ngdoc object
     * @name ComplexInputDropdownController
     * @description The controller for the complex input of type dropdown. Usually it handles dimensions with mapping values.
     * (Limited valid values the user can choose from)
     */
    class ComplexInputDropdownController {
        static get $inject() {
            return [
                'Monads',
                'segmentService',
                '$translate'
            ];
        }

        /**
         * @ngdoc property
         * @name ComplexInputDropdownController#SORTING_BY_KEY_ARRANGE_TYPES
         * @description Returns a list of arrangeTypes whose mapping follows sorting by key.
         * @returns {Array<string>}
         */
        get SORTING_BY_KEY_ARRANGE_TYPES() {
            return [
                'time_id_categories_t_weekday'
            ];
        }

        /**
         * @ngdoc property
         * @name ComplexInputDropdownController#hasSortingByKey
         * @description Determines if the arrangeType of the instance is included in the list of arrangetypes
         * with sorting by key.
         * @returns {boolean}
         */
        get hasSortingByKey() {
            return _.includes(this.SORTING_BY_KEY_ARRANGE_TYPES, this.arrangeType);
        }

        constructor(Monads, segmentService, $translate) {
            this.Monads = Monads;
            this.segmentService = segmentService;
            this.$translate = $translate;
            this.segmentsArrangeType = segmentService.getFakeSegmentArrangeType();
        }

        $onInit() {
            this.setMapping();
        }

        $onChanges(changeSet) {
            if (_.hasIn(changeSet, 'arrangeType.currentValue')) {
                this.setMapping();
            }
        }

        /**
         * @ngdoc method
         * @name ComplexInputDropdownController#getMonad
         * @description Will return the proper monad configuration taking care of segments as well
         * @returns {Object}
         */
        getMonad() {
            const segmentsArrangeType = this.segmentService.getFakeSegmentArrangeType();
            if (this.arrangeType === segmentsArrangeType) {
                const segmentTitle = this.$translate.instant('SEGMENTS_TITLE');
                const segmentGroup = 'VISITORS';
                const segmentDefaultValue = this.$translate.instant('SEGMENTS_DROPDOWN_DEFAULT_VALUE');
                return this.segmentService.getSegmentAsDimensionTemplate(segmentTitle, segmentGroup, segmentDefaultValue);
            }

            return this.Monads.layer(this.layer).monad(this.arrangeType);
        }

        /**
         * @ngdoc method
         * @name ComplexInputDropdownController#setMapping
         * @description Sets the mapping - converts it to alphabetically sorted list - based on the arrangeType
         * @returns {ComplexInputDropdownController}
         */
        setMapping() {
            const monad = this.getMonad();

            const sorted = _.chain(monad)
                .get('mapping')
                .map((value, key) => ({key, value}))
                .sortBy(({key, value}) => `${this.hasSortingByKey ? key : value}`.toLowerCase())
                .valueOf();

            // Makes sure the 'empty' mapping setting - i.e. No filter - is on top
            // in the list
            this.mapping = _.chain(sorted)
                            .remove({key: ''})
                            .concat(sorted)
                            .value();
            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDropdownController#getSelected
         * @description Returns the display title of the currently selected value
         * @returns {string}
         */
        getSelected() {
            return _.chain(this.mapping)
                    .find({
                        key: this.value
                    })
                    .get('value', '')
                    .value();
        }

        /**
         * @ngdoc method
         * @name ComplexInputDropdownController#submitValue
         * @description Sets the current value and notifies the parent component on value change
         * @param {string} value The new value
         * @returns {ComplexInputDropdownController}
         */
        submitValue(value) {
            this.value = value;
            this.onChange({$value: `${value}`});
            return this;
        }
    }

    angular.module('wtApp.arrangeTypeInput')
        .component('complexInputDropdown', {
            bindings: {
                /**
                 * The arrange type name
                 */
                arrangeType: '<',

                /**
                 * The arrange type layer. See wt-query-builder Monads.LAYERS
                 * @type {string}
                 * @default 'RAW_DATA'
                 */
                layer: '<?',

                /**
                 * Is the input read only
                 * @type {boolean}
                 * @default false
                 */
                readOnly: '<?',

                /**
                 * The current value to init the component
                 * @type {string|undefined}
                 * @default undefined
                 */
                value: '<?',

                /**
                 * Expression to evaluate when the value has changed. New value is available via $value variable.
                 * @type {Function}
                 * @default angular.noop
                 */
                onChange: '&?'
            },
            controller: ComplexInputDropdownController,
            controllerAs: '$ctrl',
            template: ['$templateCache', function($templateCache) {
                return $templateCache.get('src/components/complex-input-dropdown/complex-input-dropdown.component.html');
            }]
        });
})(window.angular, window._);
